(ns app.front.ui.visual-viewport
  "Tracks `window.visualViewport.height` (the area visible above the software
  keyboard on iOS/Android). Stores the height in re-frame app-db and provides
  utilities for reacting to viewport shrinks (keyboard open).

  Usage:
    ;; Mount once at layout level (renders nothing visible):
    [visual-viewport/observer]

    ;; Subscribe from any component:
    @(rf/subscribe [::visual-viewport/height])

    ;; One-shot shrink listener (e.g. re-scroll after keyboard):
    (visual-viewport/on-shrink-once (fn [] (scroll-to-center!)))"
  (:require [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))

;; ---------------------------------------------------------------------------
;; Events & Subscriptions
;; ---------------------------------------------------------------------------

(rf/reg-event-db ::set-height
  (fn [db [_ h]]
    (assoc db ::height h)))

(rf/reg-sub ::height
  (fn [db _]
    (or (::height db)
        (some-> (.-visualViewport js/window) .-height)
        (.-innerHeight js/window))))

;; ---------------------------------------------------------------------------
;; Shrink listener utilities
;; ---------------------------------------------------------------------------

(defn add-shrink-listener
  "Registers a callback invoked whenever the visual viewport shrinks (typically
  keyboard opening). Returns a zero-arg teardown function that removes the
  listener."
  [callback]
  (if-let [vv (.-visualViewport js/window)]
    (let [*prev-h (atom (.-height vv))
          handler (fn []
                    (let [cur-h (.-height vv)]
                      (when (< cur-h @*prev-h)
                        (callback))
                      (reset! *prev-h cur-h)))]
      (.addEventListener vv "resize" handler)
      (fn [] (.removeEventListener vv "resize" handler)))
    ;; No visualViewport API — return no-op teardown
    (fn [])))

(defn on-shrink-once
  "Like `add-shrink-listener` but the callback fires at most once and then
  automatically removes itself. Returns a teardown function for early cleanup."
  [callback]
  (if-let [vv (.-visualViewport js/window)]
    (let [*prev-h (atom (.-height vv))
          handler (fn handler-fn []
                    (let [cur-h (.-height vv)]
                      (when (< cur-h @*prev-h)
                        (.removeEventListener vv "resize" handler-fn)
                        (callback))
                      (reset! *prev-h cur-h)))]
      (.addEventListener vv "resize" handler)
      (fn [] (.removeEventListener vv "resize" handler)))
    (fn [])))

;; ---------------------------------------------------------------------------
;; Observer component
;; ---------------------------------------------------------------------------

(defn observer
  "Mounts a visual viewport height tracker. Listens to `visualViewport.resize`
  and dispatches height changes to app-db. Renders nothing visible.
  Mount once at root layout level."
  []
  (let [*teardown (atom nil)]
    (r/create-class
      {:component-did-mount
       (fn []
         (when-let [vv (.-visualViewport js/window)]
           (let [dispatch! (fn []
                             (rf/dispatch-sync [::set-height (.-height vv)]))]
             (dispatch!)
             (.addEventListener vv "resize" dispatch!)
             (reset! *teardown (fn []
                                 (.removeEventListener vv "resize" dispatch!))))))
       :component-will-unmount
       (fn []
         (when-let [teardown @*teardown]
           (teardown)))
       :reagent-render
       (fn [] nil)})))
